<template>
  <div v-loading="fetchInProgress" class="program-report">
    <template v-if="program.id">
      <el-row :gutter="5">
        <el-col :span="isRightContentVisible ? 16 : 24">
          <span class="summary-label">{{ program.name }}</span>
          <p v-if="demo" class="text-gray-300 text-sm ml-1">
            What you see below is an example of how reports are presented on
            Vestbee. To start seeing your actual reports, please contact us at
            <a href="mailto:support@vestbee.com">support@vestbee.com</a>
          </p>
          <div class="pull-right margin-top-5">
            <el-button
              icon="el-icon-arrow-left"
              size="mini"
              :disabled="!hasPreviousPeriod"
              @click="prevDateRange()"
            />
            <el-dropdown size="mini" split-button @click.native="resetNow()">
              Show {{ periodLabel }}
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item @click.native="setPeriod(Period.WEEK)">
                  1 week
                </el-dropdown-item>
                <el-dropdown-item @click.native="setPeriod(Period.WEEKx2)">
                  2 week
                </el-dropdown-item>
                <el-dropdown-item @click.native="setPeriod(Period.MONTH)">
                  1 month
                </el-dropdown-item>
                <el-dropdown-item @click.native="setPeriod(Period.ALL_TIME)">
                  All time
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <el-button
              icon="el-icon-arrow-right"
              size="mini"
              :disabled="!hasNextPeriod"
              @click="nextDateRange()"
            />
            <p v-if="DEBUG" class="date-range">
              {{ startDate | dateWithHour }} - {{ endDate | dateWithHour }}
            </p>
            <p v-else class="date-range">
              {{ startDate | date }} - {{ endDate | date }}
            </p>
          </div>
        </el-col>
        <el-col v-show="isRightContentVisible" :span="8">
          <span class="summary-label">Jul 1st - Dec 10th</span>
        </el-col>
      </el-row>

      <el-row :gutter="5">
        <!-- LEFT Panel -->
        <el-col
          :xs="24"
          :span="isRightContentVisible ? 16 : 24"
          class="left-content"
        >
          <el-row :gutter="5">
            <el-col :span="12">
              <el-card>
                <div class="stat-change">
                  <span class="stat-value">
                    {{ submittedApplications.length }}
                  </span>
                  <span class="stat-caption">Submissions</span>
                  <span
                    v-if="hasPreviousPeriod && areSubmissionsTrendingUp"
                    class="stat-trend is-trending-up"
                    ><ion-icon name="trending-up" /> +{{ submissionsDelta }}
                  </span>
                  <span
                    v-else-if="hasPreviousPeriod && !areSubmissionsTrendingUp"
                    class="stat-trend is-trending-down"
                    ><ion-icon name="trending-down" />
                    {{ submissionsDelta }}</span
                  >
                  <span v-else class="stat-trend">&nbsp;</span>
                </div>
              </el-card>
            </el-col>
            <el-col :span="12">
              <el-card>
                <div class="stat-change">
                  <span class="stat-value">
                    {{ registeredApplications.length }}
                  </span>
                  <span class="stat-caption">Registrations</span>
                  <span
                    v-if="hasPreviousPeriod && areRegistrationsTrendingUp"
                    class="stat-trend is-trending-up"
                    ><ion-icon name="trending-up" /> + {{ registrationsDelta }}
                  </span>
                  <span
                    v-else-if="hasPreviousPeriod && !areRegistrationsTrendingUp"
                    class="stat-trend is-trending-down"
                    ><ion-icon name="trending-down" />
                    {{ registrationsDelta }}</span
                  >
                  <span v-else class="stat-trend">&nbsp;</span>
                </div>
              </el-card>
            </el-col>
          </el-row>

          <el-card class="margin-top-10">
            <!-- <div class="summary-chart-actions">
              <el-radio v-model="summaryChartApplicationsType" label="submissions">Show submissions</el-radio><br/>
              <el-radio v-model="summaryChartApplicationsType" label="registrations">Show registrations</el-radio>
            </div> -->
            <SummaryChart :series="summaryChartSeries" />
          </el-card>

          <el-card class="margin-top-10">
            <AttributeStats
              title="Countries"
              series-label="Registered at this country"
              :data="countriesData"
            />
          </el-card>

          <el-card class="margin-top-10">
            <AttributeStats
              title="Sectors"
              series-label="Registered with this sector"
              :data="sectorsData"
            />
          </el-card>

          <el-card class="margin-top-10">
            <AttributeStats
              title="Product types"
              series-label="Registered with those product types"
              :data="productTypesData"
            />
          </el-card>

          <el-card class="margin-top-10">
            <AttributeStats
              title="Company stages"
              series-label="Registered with those company stages"
              :data="companyStagesData"
            />
          </el-card>

          <el-card class="margin-top-10">
            <AttributeStats
              title="Company types"
              series-label="Registered with those company types"
              :data="companyTypesData"
            />
          </el-card>

          <el-card v-if="DEBUG" class="margin-top-10">
            <pre>{{ applications | json }}</pre>
            <el-table :data="applications">
              <el-table-column>
                <template slot-scope="{ row }">
                  {{ row.startup_name }}
                </template>
              </el-table-column>
            </el-table>
          </el-card>
        </el-col>
        <!-- END LEFT Panel -->

        <!-- RIGHT Panel -->
        <el-col :xs="24" :span="8" class="right-content">
          <el-card class="submission-progress">
            <AllTimeStats />
          </el-card>
        </el-col>
        <!-- END RIGHT Panel -->
      </el-row>
    </template>

    <template v-else>
      <el-card>
        <el-alert
          title="There is no active program currently."
          type="info"
          :closable="false"
        />
      </el-card>
    </template>
  </div>
</template>

<script>
import http from '@/services/http'
import { ref, computed, watch } from '@vue/composition-api'
import SummaryChart from '@/components/organizations/program-reports/SummaryChart.vue'
import AttributeStats from '@/components/organizations/program-reports/AttributeStats.vue'
import AllTimeStats from '@/components/organizations/program-reports/AllTimeStats.vue'

import { minBy } from 'lodash'
import useDates from './compositions/useDates'
import useStats from './compositions/useStats'

const DEBUG = false

export default {
  components: {
    AllTimeStats,
    AttributeStats,
    SummaryChart,
  },
  props: {
    program: { type: Object, default: () => ({}) },
    demo: { type: Boolean, default: false },
  },
  setup(props, { root }) {
    const allApplications = ref([])
    const fetchInProgress = ref(false)
    const localProgram = ref({})

    const fetchApplications = async ({ orgId, programId } = {}) => {
      fetchInProgress.value = true
      let url
      if (props.demo) {
        url = `/organizations/${orgId}/program-demo-report`
      } else {
        url = `/organizations/${orgId}/programs/${programId}/report`
      }
      const response = await http.getAuthed(url)
      fetchInProgress.value = false
      return response.data.applications
    }

    watch('program', async (p) => {
      localProgram.value = { ...p }
      if (!p.id) {
        return
      }
      allApplications.value = await fetchApplications({
        orgId: p.organization_id,
        programId: p.id,
      })

      if (props.demo) {
        // without this, it won't be possible to click at "Previous dates" button
        localProgram.value.activated_at = minBy(
          allApplications.value,
          'created_at',
        ).created_at
        localProgram.value.deactivated_at = null
      }
    })

    const {
      Period,
      period,
      setPeriod,
      periodLabel,
      prevDateRange,
      nextDateRange,
      resetNow,
      getDateDeltaForPeriod,
      currentDate,
      hasNextPeriod,
      hasPreviousPeriod,
      startDate,
      endDate,
      datesRange,
    } = useDates({ DEBUG, program: localProgram, allApplications })

    const applications = computed(() => {
      const start = startDate.value.toISOString()
      const end = endDate.value.toISOString()
      return allApplications.value.filter((application) => {
        const submittedWithinDates =
          start <= application.submitted_at && application.submitted_at <= end
        return submittedWithinDates
      })
    })

    const submittedApplicationsForPreviousPeriod = computed(() => {
      if (!hasPreviousPeriod.value) {
        return []
      }
      const delta = getDateDeltaForPeriod(period.value)
      const start = startDate.value.clone().subtract(delta).toISOString()
      const end = endDate.value.clone().subtract(delta).toISOString()
      return allApplications.value.filter((a) => {
        return start <= a.submitted_at && a.submitted_at <= end
      })
    })

    const registeredApplicationsForPreviousPeriod = computed(() => {
      if (!hasPreviousPeriod.value) {
        return []
      }
      const delta = getDateDeltaForPeriod(period.value)
      const start = startDate.value.clone().subtract(delta).toISOString()
      const end = endDate.value.clone().subtract(delta).toISOString()
      return allApplications.value.filter((a) => {
        return start <= a.created_at && a.created_at <= end
      })
    })

    const submissionsDelta = computed(() => {
      return (
        submittedApplications.value.length -
        submittedApplicationsForPreviousPeriod.value.length
      )
    })

    const areSubmissionsTrendingUp = computed(() => {
      return submissionsDelta.value >= 0
    })

    const registrationsDelta = computed(() => {
      return (
        registeredApplications.value.length -
        registeredApplicationsForPreviousPeriod.value.length
      )
    })

    const areRegistrationsTrendingUp = computed(() => {
      return registrationsDelta.value >= 0
    })

    const registeredApplications = computed(() => {
      const start = startDate.value.toISOString()
      const end = endDate.value.toISOString()
      return allApplications.value.filter((a) => {
        return start <= a.created_at && a.created_at <= end
      })
    })

    const submittedApplications = computed(() => {
      const start = startDate.value.toISOString()
      const end = endDate.value.toISOString()
      return allApplications.value.filter((a) => {
        return start <= a.submitted_at && a.submitted_at <= end
      })
    })

    const summaryChartApplicationsType = ref('submissions')
    const summaryChartApplications = computed(() => {
      if (summaryChartApplicationsType.value === 'submissions') {
        return submittedApplications.value
      } else {
        return registeredApplications.value
      }
    })
    const dateAttr = computed(() => {
      if (summaryChartApplicationsType.value === 'submissions') {
        return 'submitted_at'
      } else {
        return 'created_at'
      }
    })

    const {
      summaryChartSeries,
      countriesData,
      sectorsData,
      productTypesData,
      companyStagesData,
      companyTypesData,
    } = useStats({
      DEBUG,
      applications: summaryChartApplications,
      dateAttr,
      datesRange,
    })

    const result = {
      DEBUG,

      Period,
      resetNow,
      currentDate,
      prevDateRange,
      nextDateRange,
      hasNextPeriod,
      hasPreviousPeriod,
      startDate,
      endDate,
      datesRange,
      setPeriod,
      periodLabel,

      fetchInProgress,
      applications,
      submittedApplications,
      registeredApplications,

      areSubmissionsTrendingUp,
      submissionsDelta,
      areRegistrationsTrendingUp,
      registrationsDelta,

      summaryChartApplicationsType,
      summaryChartSeries,
      countriesData,
      sectorsData,
      productTypesData,
      companyStagesData,
      companyTypesData,

      isRightContentVisible: false,
    }
    window.vm = root
    window.data = result
    return result
  },
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';
@import '~assets/css/vars.scss';

.program-report {
  .submission-progress {
    min-height: 300px;
  }

  .summary-chart-actions {
    .el-radio {
      color: $grey90;
    }
  }

  .summary-label {
    font-size: 16px;
    font-weight: 300;
    color: $grey90;
    margin: 10px 0 10px 5px;
    display: inline-block;
  }

  .date-range {
    color: $grey90;
    font-size: 12px;
    font-weight: 300;
    text-align: right;
    padding-top: 8px;
  }

  .stat-change {
    .stat-value {
      display: block;
      font-size: 30px;
      font-weight: 300;
      color: $grey90;
    }
    .stat-caption {
      display: block;
      font-size: 12px;
      color: $grey120;
    }
    .stat-trend {
      display: block;
      font-size: 12px;
      &.is-trending-up {
        color: $green;
      }
      &.is-trending-down {
        color: $danger;
      }
    }
  }
}
</style>
